import React, { Fragment, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PlusOutlined } from '@ant-design/icons'

import { ExtendedTable, SubheaderToolbarDivider } from '@app/components'
import { LntButton } from '@app/components/base'
import { TableSearchButton } from '@app/components/TableSettingsToolbar'

import { BUTTON_TYPE, SIZE } from '@app/constants'
import { TRANSLATIONS_CONFIG_KEY } from '@app/constants/queryKeys'

import { useNotification, usePaginationParams, useRemoveTableRowButton } from '@app/hooks'
import { getTranslationsConfigMonitoringTypesDataFromResponse } from '@app/utils/responseAdapters/getTranslationsConfigDataFromResponse'

import {
  deleteTranslationsMonitoringTypes,
  getTranslationsConfigMonitoringTypes
} from '@app/api/routes/translationsConfig'

import { getColumns } from './utils'

import type { MonitoringTypeTabProps } from './types'
import type { TranslationsConfigMonitoringTypesDataSource } from '@app/types/dataSource/TranslationsConfigDataSource'

const columns = getColumns()

export const MonitoringTypeTab: React.FC<MonitoringTypeTabProps> = props => {
  const { onOpenAddMonitoringTypesDrawer } = props

  const queryClient = useQueryClient()
  const notification = useNotification()

  const { pagination, setPagination, paginationParams } = usePaginationParams()

  const { data: translationsMonitoringTypesData, isLoading: isTranslationsMonitoringTypesDataLoading } = useQuery({
    queryKey: [TRANSLATIONS_CONFIG_KEY.monitoringTypes],
    queryFn: async () => await getTranslationsConfigMonitoringTypes({ ...paginationParams })
  })

  const deleteTranslationsMonitoringTypesMutation = useMutation({
    mutationFn: deleteTranslationsMonitoringTypes,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [TRANSLATIONS_CONFIG_KEY.monitoringTypes] })

      notification.success({
        message: 'Выбранный тип мониторинга удален из транслирования.'
      })
    }
  })

  const dataSource = useMemo(
    () => getTranslationsConfigMonitoringTypesDataFromResponse(translationsMonitoringTypesData),
    [translationsMonitoringTypesData]
  )

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handleDeleteMonitoringType = (rowKey: string): void => {
    deleteTranslationsMonitoringTypesMutation.mutate({ monitoringTypeSapId: rowKey })
  }

  useRemoveTableRowButton({
    columns,
    onClick: rowKey => handleDeleteMonitoringType(String(rowKey))
  })

  const toolbar = [
    <Fragment key='add-monitoring-types'>
      <LntButton
        icon={ <PlusOutlined /> }
        type={ BUTTON_TYPE.primary }
        size={ SIZE.small }
        onClick={ onOpenAddMonitoringTypesDrawer }
      >
        Тип мониторинга
      </LntButton>

      <SubheaderToolbarDivider />
    </Fragment>,

    // TODO: Добавить табличный фильтр
    <TableSearchButton key='search-monitoring-types' />
  ]

  return (
    <ExtendedTable<TranslationsConfigMonitoringTypesDataSource>
      loading={ isTranslationsMonitoringTypesDataLoading }
      toolbar={ toolbar }
      columns={ columns }
      dataSource={ dataSource }
      pagination={ {
        ...pagination,
        total: translationsMonitoringTypesData?.count,
        onChange: handlePaginationChange
      } }
    />
  )
}
